// AyaPriveHero — Accueil du mode AYA Privé, reproduction fidèle de la maquette
// « Interface premium AYA » (burger menu, identité sur une ligne, bloc accueil
// compact, zone vocale centrale sur fond jungle).
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const BurgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

// Silhouette simplifiée de la Côte d'Ivoire (mini-carte → retour AYA Search).
const CivMapIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="currentColor" aria-hidden="true">
    <path d="M12,16 L24,12 L38,10 L50,13 L54,22 L52,32 L56,40 L50,48 Q42,46 36,52 Q28,58 20,52 L14,44 L16,34 L10,26 Z" />
  </svg>
);

export default function AyaPriveHero({ userName, onTalk, voiceState, onOpenSettings, onScrollToInput, onMenu }) {
  const navigate = useNavigate();

  // L'auto-scroll du chat pousse vers le bas : on force le retour en haut du hero.
  useEffect(() => {
    const toTop = () => { const sc = document.querySelector('[data-testid="messages-container"]'); if (sc) sc.scrollTop = 0; };
    const t1 = setTimeout(toTop, 250);
    const t2 = setTimeout(toTop, 900);
    const t3 = setTimeout(toTop, 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const listening = voiceState === "listening" || String(voiceState || "").includes("listening");
  const thinking = voiceState === "processing";

  return (
    <div className="aph" data-testid="aya-prive-hero">
      {/* HEADER — burger + identité sur UNE ligne */}
      <div className="aph-header">
        <button className="aph-hbtn aph-burger" data-testid="aph-menu-btn" onClick={onMenu} aria-label="Menu">
          <BurgerIcon />
        </button>
        <div className="aph-id">
          <div className="aph-id-title">
            AYA <span className="aph-id-prive" data-testid="aya-prive-label">Privé</span>
            <span className="aph-id-sub">· Votre assistant IA national 🇨🇮</span>
          </div>
        </div>
        <button className="aph-hbtn aph-bell" data-testid="aph-notifications-btn" onClick={() => navigate("/mon-compte")} aria-label="Notifications">
          🔔<span className="aph-bell-dot" />
        </button>
        <button className="aph-hbtn aph-profile" data-testid="aph-profile-btn" onClick={() => navigate("/mon-compte")}>
          👤 Profil
        </button>
      </div>

      {/* BLOC ACCUEIL — compact, sans cartes indicateurs */}
      <div className="aph-welcome" data-testid="aph-welcome-block">
        <div className="aph-wel-top">
          <div className="aph-wel-txt">
            <div className="aph-wel-title">Bonjour {userName || "Invité"} ! 👋</div>
            <div className="aph-wel-sub">Bienvenue sur AYA 🇨🇮 · <span className="aph-gold">Profil ✨</span></div>
            <div className="aph-wel-q">Que puis-je faire pour vous aujourd'hui ?</div>
          </div>
          <button className="aph-wel-btn aph-ci-btn" data-testid="aph-search-map-btn" onClick={() => navigate("/search")} aria-label="Retour à AYA Search" title="Retour à AYA Search">
            <CivMapIcon size={19} />
          </button>
          <button className="aph-wel-btn" data-testid="aph-settings-btn" onClick={onOpenSettings || (() => navigate("/mon-compte?tab=aya_prive"))} aria-label="Paramètres">⚙️</button>
          <button className="aph-wel-btn" data-testid="aph-collapse-btn" onClick={onScrollToInput} aria-label="Aller à la saisie" style={{ color: "#ECF5EE" }}>▲</button>
        </div>
      </div>

      {/* ZONE CENTRALE VOCALE — fond jungle fourni par la page (.aya-prive-shell) */}
      <div className={`aph-voice${listening ? " aph-listening" : ""}`} data-testid="aph-voice-zone">
        <div className="aph-mask-stage" aria-hidden="true">
          <span className="aph-waves aph-waves-left">
            <i /><i /><i /><i />
          </span>
          <span className="aph-neon-ring" />
          <img src="/aya/splash.png" alt="Masque AYA" className="aph-mask-img" />
          <span className="aph-waves aph-waves-right">
            <i /><i /><i /><i />
          </span>
        </div>
        <div className="aph-voice-title">Parlez à <span className="aph-aya">AYA</span></div>
        <div className="aph-voice-sub">Je peux rechercher, acheter, réserver, payer ou effectuer une démarche pour vous.</div>
        <button type="button" className="aph-talk-btn" onClick={onTalk} data-testid="aph-talk-btn">
          <span className="aph-talk-mic">🎙️</span>
          <span>
            <span className="aph-talk-label">
              {listening ? "AYA vous écoute…" : thinking ? "AYA réfléchit…" : "Appuyer pour parler"}
            </span>
            {(listening || thinking) && <span className="aph-talk-state">{listening ? "Parlez naturellement" : "Un instant"}</span>}
          </span>
        </button>
        <div className="aph-voice-hint">Ou tapez votre message ci-dessous</div>
      </div>
    </div>
  );
}

// Masque réduit PERSISTANT en conversation : petit masque + anneau néon fixés
// en haut, les réponses défilent en dessous (sticky dans .civ-messages).
export function AyaPriveMiniMask({ onMenu, listening }) {
  return (
    <div className={`aphm${listening ? " aphm-listening" : ""}`} data-testid="aya-prive-mini-mask">
      <button className="aphm-burger" data-testid="aphm-menu-btn" onClick={onMenu} aria-label="Menu">
        <BurgerIcon />
      </button>
      <div className="aphm-center">
        <span className="aphm-stage">
          <span className="aphm-ring" />
          <img src="/aya/splash.png" alt="AYA" className="aphm-img" />
        </span>
        <span className="aphm-label">AYA <em>Privé</em></span>
      </div>
      <span className="aphm-spacer" aria-hidden="true" />
    </div>
  );
}
